import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Alert } from './ui/alert';
import {
  Camera, Shield, ShieldCheck, AlertTriangle, CheckCircle, Bell, Archive, TrendingUp,
  Activity, BarChart3, Filter, Download, Search, Eye, AlertCircle, Flame,
  UserX, Car, Wifi, WifiOff, Maximize2, RefreshCw, Terminal,
  Zap, ArrowRight, UserCheck, Lock, Globe, Clock, Target, Trash2, Settings,
  BarChart, Layers, Database, Cpu, HardDrive, Network
} from 'lucide-react';

import { motion, AnimatePresence } from 'framer-motion';
import { useApiHealth, useDetectionStats, useAlerts } from '../hooks/useApi';
import { AgentConfigOverlay } from './AgentConfigOverlay';
import LiveStreamViewer from './LiveStreamViewer';

// --- Shared Glass Components ---

const GlassPanel: React.FC<{ children: React.ReactNode; className?: string; title?: string; icon?: any; headerAction?: React.ReactNode }> = ({ children, className = '', title, icon: Icon, headerAction }) => (
  <div className={`bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 ${className}`}>
    {(title || Icon || headerAction) && (
      <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-400" />}
          {title && <h3 className="text-sm font-bold tracking-tight text-blue-100/90 uppercase pr-4">{title}</h3>}
        </div>
        {headerAction}
      </div>
    )}
    <div className="p-6">
      {children}
    </div>
  </div>
);

const StatCard: React.FC<{ label: string; value: string; sub: string; icon: any; color: string; bgColor: string }> = ({ label, value, sub, icon: Icon, color, bgColor }) => (
  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 relative overflow-hidden group hover:bg-white/10 transition-all">
    <div className={`absolute top-0 right-0 w-24 h-24 ${bgColor} blur-[40px] opacity-20 -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-700`} />
    <div className="flex items-center gap-4 relative z-10">
      <div className={`w-12 h-12 rounded-xl ${bgColor} border border-white/10 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1">{label}</p>
        <h4 className="text-2xl font-black text-white">{value}</h4>
        <p className="text-[10px] text-blue-100/30 mt-1 font-medium">{sub}</p>
      </div>
    </div>
  </div>
);

// --- Sub-Components ---

function DashboardAnalytics() {
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem('csense_auth_token');
        const response = await fetch('/api/dashboard/analytics', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAnalyticsData(data);
        }
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-48 bg-white/5 rounded-2xl" /></div>;

  const usecaseData = analyticsData?.usecase_detections || {};
  const usecaseColors: any = {
    'fire-detection': 'bg-red-500',
    'fall-detection': 'bg-orange-500', 
    'anpr': 'bg-blue-500',
    'worker-time-tracking': 'bg-emerald-500',
    'crowd-detection': 'bg-purple-500',
    'restricted-area': 'bg-amber-500',
    'intrusion-detection': 'bg-indigo-500'
  };

  const usecaseLabels: any = {
    'fire-detection': 'Fire & Smoke',
    'fall-detection': 'Patient Fall',
    'anpr': 'License Plate',
    'worker-time-tracking': 'Ward Activity',
    'crowd-detection': 'Patient Loitering',
    'restricted-area': 'PPE Violation',
    'intrusion-detection': 'Intrusion'
  };

  const totalDetections = Object.values(usecaseData).reduce((sum: number, count: any) => sum + count, 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <GlassPanel title="Detection Pulse" icon={Activity}>
        <div className="space-y-4">
          {Object.entries(usecaseData).map(([usecase, count]: [string, any]) => {
            const percentage = totalDetections > 0 ? (count / totalDetections) * 100 : 0;
            return (
              <div key={usecase} className="group cursor-pointer">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-bold text-blue-100/70 group-hover:text-white transition-colors">
                    {usecaseLabels[usecase] || usecase}
                  </span>
                  <span className="text-xs font-mono text-blue-100/40">{count} events</span>
                </div>
                <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    className={`h-full ${usecaseColors[usecase] || 'bg-blue-500'}`} 
                  />
                </div>
              </div>
            );
          })}
        </div>
      </GlassPanel>

      <GlassPanel title="Intelligence Distribution" icon={BarChart3}>
        <div className="flex items-center justify-center h-full min-h-[200px]">
          {/* Visual representation placeholder */}
          <div className="text-center">
            <BarChart className="w-12 h-12 text-blue-500/20 mx-auto mb-4" />
            <p className="text-[10px] text-blue-100/30 uppercase tracking-[0.2em]">Neural Engine Analysis</p>
            <p className="text-xs text-blue-100/50 mt-2 italic px-8 font-light">Distribution mapping is based on historical behavioral patterns across all deployed nodes.</p>
          </div>
        </div>
      </GlassPanel>
    </div>
  );
}

function LiveCameraFeed({ camera, onClick }: any) {
  const [streamFailed, setStreamFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const token = localStorage.getItem('csense_auth_token');
  const streamSrc = camera.id ? `/api/cameras/${camera.id}/live-stream?token=${encodeURIComponent(token || '')}&r=${reloadKey}` : '';

  useEffect(() => {
    setStreamFailed(false);
    setReloadKey(k => k + 1);
  }, [camera.id]);

  return (
    <div
      onClick={onClick}
      className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden group cursor-pointer hover:border-blue-500/50 transition-all shadow-xl"
    >
      <div className="relative aspect-video bg-black flex items-center justify-center">
        {streamSrc && !streamFailed ? (
          <img
            key={reloadKey}
            src={streamSrc}
            alt="Feed"
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setStreamFailed(true)}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <Camera className="w-8 h-8 text-blue-100/10" />
            <span className="text-[9px] font-bold uppercase tracking-widest text-blue-100/20">{streamFailed ? 'Camera Unreachable' : 'Connecting...'}</span>
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        {/* Overlays */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <div className={`px-2 py-0.5 rounded-md text-[10px] font-black text-white flex items-center gap-1.5 shadow-lg ${streamFailed ? 'bg-red-600' : 'bg-blue-600'}`}>
            <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
            {streamFailed ? 'OFFLINE' : 'LIVE'}
          </div>
          {!streamFailed && (
            <div className="px-2 py-0.5 rounded-md bg-black/50 backdrop-blur-md border border-white/10 text-[9px] font-bold text-blue-200">
              {camera.fps || '30'} FPS
            </div>
          )}
        </div>

        <div className="absolute bottom-3 left-3 text-white">
          <h4 className="text-xs font-black tracking-tight">{camera.name}</h4>
          <p className="text-[9px] text-blue-200/50 uppercase tracking-widest">{camera.location || 'Default Sector'}</p>
        </div>

        <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-all translate-y-2 group-hover:translate-y-0">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shadow-lg">
            <Maximize2 className="w-4 h-4 text-white" />
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Main Dashboard Component ---

export function Dashboard({ setupData, onAddAlert, backendUrl, detectionActive, fetchDetectionStats, fetchActiveStreams, onNavigateToTab, showAgentOverlay = false, onSetShowAgentOverlay }: any) {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'overview' | 'consumption'>('dashboard');
  const [selectedCamera, setSelectedCamera] = useState<any>(null);
  const [subscriptionData, setSubscriptionData] = useState<any>({ status: 'active', plan: 'small', cameras_used: 0, cameras_limit: 10 });
  const [dashboardStats, setDashboardStats] = useState<any>({ total_cameras: 0, active_detections: 0, alerts_sent_today: 0, total_alerts: 0 });
  const [realCameras, setRealCameras] = useState<any[]>([]);

  const { stats } = useDetectionStats();
  const { alerts: backendAlerts, loading: alertsLoading } = useAlerts();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('csense_auth_token');
        const [statsRes, userRes, camerasRes] = await Promise.all([
          fetch('/api/stats', { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch('/api/auth/me', { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch('/api/cameras', { headers: { 'Authorization': `Bearer ${token}` } })
        ]);

        if (statsRes.ok) setDashboardStats(await statsRes.json());
        if (userRes.ok) {
           const userData = await userRes.json();
           setSubscriptionData({
             status: userData.subscription_status || 'active',
             plan: userData.subscription_plan || 'small',
             cameras_used: dashboardStats.total_cameras || 0,
             cameras_limit: userData.plan_limits?.cameras || 10,
             plan_limits: userData.plan_limits || { cameras: 10, usecases: 5, subusers: 5 }
           });
        }
        if (camerasRes.ok) {
          const data = await camerasRes.json();
          setRealCameras(data.cameras || []);
        }
      } catch (e) { console.error(e); }
    };
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0b14] text-white p-6 space-y-8 pb-32">
      {/* Dynamic Background Atmosphere */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/5 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-500/5 blur-[100px]" />
      </div>

      {/* Subscription Alerts */}
      <AnimatePresence>
        {subscriptionData.status === 'suspended' && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
            <Alert className="bg-red-500/10 border-red-500/20 text-red-400 p-4 rounded-2xl backdrop-blur-xl">
              <AlertCircle className="w-5 h-5 mr-3" />
              <div className="flex-1">
                <p className="text-sm font-bold">Node Synchronization Suspended</p>
                <p className="text-xs text-red-200/60 mt-1">Contact engineering support to restore system integrity.</p>
              </div>
              <Button size="sm" variant="outline" className="border-red-500/20 text-red-500 bg-transparent hover:bg-red-500/10 rounded-xl" onClick={() => onNavigateToTab?.('support')}>Support</Button>
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tactical Navigation */}
      <div className="relative z-10 flex flex-wrap items-center justify-between gap-4">
        <div className="flex bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-1 shadow-2xl">
          {[
            { id: 'dashboard', label: 'Command', icon: BarChart3 },
            { id: 'overview', label: 'Nodes', icon: Camera },
            { id: 'consumption', label: 'Intelligence', icon: TrendingUp }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest transition-all ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-[0_0_20px_rgba(37,99,235,0.4)]' : 'text-blue-100/40 hover:text-white hover:bg-white/5'}`}
            >
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="px-4 py-2 bg-white/5 rounded-xl border border-white/10 flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.5)]" />
            <span className="text-[10px] font-black text-blue-100 uppercase tracking-widest">System Optimal</span>
          </div>
        </div>
      </div>

      <div className="relative z-10 space-y-8">
        {activeTab === 'dashboard' && (
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
            <div className="xl:col-span-3 space-y-8">
              {/* Tactical Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard label="Active Nodes" value={dashboardStats.total_cameras.toString()} sub="Cognitive sensors" icon={Camera} color="text-blue-400" bgColor="bg-blue-500/10" />
                <StatCard label="Active Engines" value={dashboardStats.active_detections.toString()} sub="Real-time logic" icon={Shield} color="text-purple-400" bgColor="bg-purple-500/10" />
                <StatCard label="Daily Alerts" value={dashboardStats.alerts_sent_today.toString()} sub="Triage complete" icon={Bell} color="text-emerald-400" bgColor="bg-emerald-500/10" />
                <StatCard label="Total Saved" value={dashboardStats.total_alerts.toString()} sub="Historical archives" icon={Database} color="text-amber-400" bgColor="bg-amber-500/10" />
              </div>

              {/* Feed & Analytics */}
              <div className="space-y-6">
                <div className="flex items-center justify-between pr-2">
                   <h2 className="text-lg font-black tracking-tighter uppercase text-blue-100/80">Visual Intelligence Feeds</h2>
                   <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest cursor-pointer hover:underline" onClick={() => onNavigateToTab?.('cameras')}>Advanced View</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {realCameras.slice(0, 2).map((cam, i) => (
                    <LiveCameraFeed key={i} camera={cam} onClick={() => setSelectedCamera(cam)} />
                  ))}
                  {realCameras.length === 0 && (
                    <div className="col-span-2 py-20 text-center border-2 border-dashed border-white/5 rounded-3xl">
                       <p className="text-blue-100/20 text-sm font-light italic">No cognitive nodes deployed. Initialization required.</p>
                       <Button variant="ghost" className="mt-4 text-blue-400 hover:text-blue-300" onClick={() => onNavigateToTab?.('cameras')}>Deploy Node <ArrowRight className="w-4 h-4 ml-2" /></Button>
                    </div>
                  )}
                </div>
              </div>

              <DashboardAnalytics />
            </div>

            <div className="xl:col-span-1">
              <GlassPanel title="Tactical Feed" icon={Terminal} className="h-full">
                <div className="space-y-6 max-h-[800px] overflow-y-auto pr-2 custom-scrollbar">
                  {alertsLoading && <div className="text-center py-10 opacity-20"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /> <p className="text-[10px] uppercase font-bold">Syncing Feed...</p></div>}
                  {backendAlerts?.slice(0, 15).map((alert: any, i: number) => (
                    <div key={i} className="flex gap-4 group">
                      <div className="flex flex-col items-center">
                        <div className={`w-2 h-2 rounded-full mt-1.5 ${alert.priority === 'critical' ? 'bg-red-500 shadow-[0_0_10px_#ef4444]' : alert.priority === 'high' ? 'bg-orange-500' : 'bg-blue-500'}`} />
                        <div className="w-[1px] flex-1 bg-white/5 my-1" />
                      </div>
                      <div className="pb-6">
                        <div className="flex justify-between items-center mb-1">
                          <span className={`text-[10px] font-black uppercase tracking-widest ${alert.priority === 'critical' ? 'text-red-400' : 'text-blue-200'}`}>{alert.title || alert.type}</span>
                          <span className="text-[9px] font-mono text-blue-100/20">{new Date(alert.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <p className="text-xs text-blue-100/60 leading-relaxed font-light">{alert.message}</p>
                        {alert.camera && <div className="mt-2 text-[9px] text-blue-500/40 uppercase font-black tracking-tighter">Sector: {alert.camera}</div>}
                      </div>
                    </div>
                  ))}
                  {!alertsLoading && (!backendAlerts || backendAlerts.length === 0) && (
                    <div className="text-center py-20 opacity-10">
                       <Shield className="w-12 h-12 mx-auto mb-4" />
                       <p className="text-[10px] uppercase font-black">No recent events logged</p>
                    </div>
                  )}
                </div>
              </GlassPanel>
            </div>
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="space-y-8">
            <h1 className="text-3xl font-black tracking-tighter">Deployment Overview</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <GlassPanel title="Infrastructure Status" className="md:col-span-2">
                 <div className="space-y-8">
                   <div className="grid grid-cols-2 gap-8">
                      <div>
                        <p className="text-[10px] text-blue-100/30 uppercase tracking-widest font-bold mb-3">Processor Load</p>
                        <div className="flex items-center gap-4">
                           <h3 className="text-3xl font-black text-white">42%</h3>
                           <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 w-[42%]" />
                           </div>
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] text-blue-100/30 uppercase tracking-widest font-bold mb-3">Memory Commitment</p>
                        <div className="flex items-center gap-4">
                           <h3 className="text-3xl font-black text-white">68%</h3>
                           <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                              <div className="h-full bg-purple-500 w-[68%]" />
                           </div>
                        </div>
                      </div>
                   </div>
                   
                   <div className="border-t border-white/5 pt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {[
                        { label: 'Uptime', val: '99.99%', icon: Clock, color: 'text-emerald-400' },
                        { label: 'Latency', val: '24ms', icon: Cpu, color: 'text-blue-400' },
                        { label: 'Traffic', val: '1.2 GB/s', icon: Network, color: 'text-indigo-400' },
                        { label: 'Health', val: 'Optimal', icon: ShieldCheck, color: 'text-emerald-400' }
                      ].map((item, i) => (
                        <div key={i} className="p-4 bg-white/5 rounded-2xl border border-white/5">
                           <item.icon className={`w-5 h-5 mb-3 ${item.color}`} />
                           <p className="text-[10px] text-blue-100/30 uppercase font-black">{item.label}</p>
                           <h4 className="text-lg font-bold text-white">{item.val}</h4>
                        </div>
                      ))}
                   </div>
                 </div>
              </GlassPanel>

              <GlassPanel title="Subscription Integrity">
                 <div className="space-y-6">
                    <div className="flex items-center justify-between">
                       <h3 className="text-xl font-black">{subscriptionData.plan.toUpperCase()} PLAN</h3>
                       <Badge className="bg-emerald-500/20 text-emerald-400 border-none px-3 font-black">ACTIVE</Badge>
                    </div>
                    <div className="space-y-4">
                       <div className="space-y-2">
                          <div className="flex justify-between text-xs font-bold text-blue-100/60 uppercase">
                             <span>Node Capacity</span>
                             <span>{subscriptionData.cameras_used}/{subscriptionData.cameras_limit}</span>
                          </div>
                          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                             <div className="h-full bg-blue-500" style={{ width: `${(subscriptionData.cameras_used / subscriptionData.cameras_limit) * 100}%` }} />
                          </div>
                       </div>
                       <p className="text-[10px] text-blue-100/30 leading-relaxed italic">System automatically optimizes resource allocation based on active detection engines.</p>
                       <Button className="w-full bg-blue-600 hover:bg-blue-500 text-white font-black py-6 rounded-2xl shadow-xl mt-4" onClick={() => onNavigateToTab?.('settings')}>Manage Subscription</Button>
                    </div>
                 </div>
              </GlassPanel>
            </div>
          </div>
        )}

        {activeTab === 'consumption' && (
          <div className="space-y-8">
            <div className="flex justify-between items-center">
               <h1 className="text-3xl font-black tracking-tighter">Environment Consumption</h1>
               <Button variant="outline" className="border-white/10 text-blue-100/60 hover:bg-white/5 rounded-xl" onClick={() => window.print()}><Download className="w-4 h-4 mr-2" /> Export Report</Button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
               <StatCard label="Neural Engine Load" value="58.2 GFLOPS" sub="Aggregate processing power" icon={Zap} color="text-amber-400" bgColor="bg-amber-500/10" />
               <StatCard label="Storage Commitment" value="2.1 TB" sub="Encrypted archives" icon={HardDrive} color="text-blue-400" bgColor="bg-blue-500/10" />
               <StatCard label="API Throughput" value="1.5k req/s" sub="Intelligence relay" icon={Terminal} color="text-purple-400" bgColor="bg-purple-500/10" />
            </div>

            <GlassPanel title="System Analytics Depth" icon={Layers}>
               <div className="py-20 text-center">
                  <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-blue-500/20">
                     <TrendingUp className="w-10 h-10 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Deep Consumption Metrics</h3>
                  <p className="text-blue-100/40 text-sm max-w-md mx-auto mb-8 font-light leading-relaxed">Integrated mapping of system load and resource allocation is continuously updated every 60 seconds.</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
                    {[
                      { l: 'Network In', v: '850 Mbps' },
                      { l: 'Network Out', v: '120 Mbps' },
                      { l: 'IOPS', v: '12.4k' },
                      { l: 'Threads', v: '2.5k' }
                    ].map((m, i) => (
                      <div key={i}>
                         <div className="text-2xl font-black text-white">{m.v}</div>
                         <div className="text-[10px] text-blue-100/30 uppercase mt-1">{m.l}</div>
                      </div>
                    ))}
                  </div>
               </div>
            </GlassPanel>
          </div>
        )}
      </div>

      {/* Overlays */}
      {showAgentOverlay && (
        <AgentConfigOverlay
          onClose={() => onSetShowAgentOverlay?.(false)}
          onNavigateToAgents={() => {
            onSetShowAgentOverlay?.(false);
            onNavigateToTab?.('agents');
          }}
        />
      )}
      
      {selectedCamera && (
        <LiveStreamViewer
          rtspUrl={selectedCamera.rtsp_url || selectedCamera.rtsp_url_display || ''}
          cameraName={selectedCamera.name}
          cameraId={selectedCamera.id}
          onClose={() => setSelectedCamera(null)}
        />
      )}
    </div>
  );
}